import EmailIcon from '@mui/icons-material/Email';
import React, { useContext, useState } from 'react';

import { BadRequestError, combineErrorsAndIssues, NotFoundError } from '../../api/apiErrors';

import { AppNameEnum } from '../../api/apiTypes.shim';

import { BackdropSpinner } from '../../components/BackdropSpinner/BackdropSpinner';
import { Box } from '../../components/Box/Box';
import { Button } from '../../components/Button/Button';
import { ErrorDisplay } from '../../components/ErrorDisplay/ErrorDisplay';
import { InputAdornment } from '../../components/InputAdornment/InputAdornment';
import { RouterLink } from '../../components/Link/RouterLink';
import { TextField } from '../../components/TextField/TextField';
import { Typography } from '../../components/Typography/Typography';
import { useOurSnackbar } from '../../utils/snackUtils';

import { AuthContext } from './authContext';

export interface ForgotPasswordProps {
  appName: AppNameEnum;
}

export const ForgotPassword = (props: ForgotPasswordProps): React.ReactElement => {
  const { appName } = props;

  const { forgotPassword } = useContext(AuthContext);
  const { enqueueSnackbar } = useOurSnackbar();
  const [email, setEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const handleSubmit = async () => {
    setIsSaving(true);

    try {
      if (!email) {
        setValidationErrors(['email is required']);
        setIsSaving(false);
        return;
      }

      await forgotPassword({ appName, email });
      setValidationErrors([]);
      enqueueSnackbar('An email has been sent to allow you to reset your password');
    } catch (err) {
      if (err instanceof BadRequestError) {
        const { nonFieldErrors, fieldIssues } = err.responseData || {};
        setValidationErrors(combineErrorsAndIssues(nonFieldErrors, fieldIssues));
      } else if (err instanceof NotFoundError) {
        setValidationErrors([`No active account found for email: ${email}`]);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <BackdropSpinner open={isSaving} />
      {!!validationErrors.length && (
        <ErrorDisplay errors={validationErrors} invertColors padding={1} sx={{ margin: 1, borderRadius: 2 }} />
      )}
      <TextField
        autoFocus
        fullWidth
        invertColors
        required
        autoComplete='username'
        label='email'
        size='medium'
        value={email}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position='start'>
                <EmailIcon />
              </InputAdornment>
            )
          }
        }}
        onChange={(e) => setEmail(e.target.value || '')}
        onEnter={handleSubmit}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          marginY: 1
        }}>
        <RouterLink color='primaryInverted' to='../login' variant='body1Bold'>
          Back to login
        </RouterLink>
        <Button type='button' variant='contained' color='primaryInverted' disabled={!email} onClick={handleSubmit}>
          <Typography variant='body1Bold' text='Submit' />
        </Button>
      </Box>
    </>
  );
};
